import { useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useInactivityLogout } from '../hooks/useInactivityLogout'
import { getSessionTimeoutMs } from '../services/sessionActivity'
import InactivityWarningModal from './InactivityWarningModal'

// Read warning-window configuration from environment variables (default 60s).
// The total session timeout comes from sessionActivity (the platform-admin
// -configured value, #181) so the in-tab logout and the on-return idle check
// (#517) share one source of truth.
const SESSION_WARNING_SECONDS = parseInt(import.meta.env.VITE_SESSION_WARNING_SECONDS, 10) || 60
const warningDurationMs = SESSION_WARNING_SECONDS * 1000

// Floor for the actual inactivity window (before the warning modal appears).
// The admin-configured total timeout can be as low as 1 minute (#181), which
// is <= the warning window — without a floor, `inactivityTimeoutMs` would be
// clamped to 0 by Math.max(0, ...) below, firing the warning immediately on
// mount and again on every "Stay logged in" click (activity is ignored while
// the warning is showing), logging every user out in a loop every 60s. A 15s
// floor guarantees real inactivity time exists regardless of how low the
// total timeout or how high the warning window are configured.
const MIN_INACTIVITY_TIMEOUT_MS = 15 * 1000

/**
 * Wrapper component that monitors user inactivity and automatically
 * logs out users after a configurable period of no activity.
 *
 * The total timeout is the platform-wide idle-session setting configured by
 * super/platform admins (#181, `GET /platform/settings`), fetched on app boot
 * (see `AuthContext.checkUser` / `sessionActivity.fetchSessionTimeoutConfig`).
 * It falls back to VITE_SESSION_TIMEOUT_MINUTES (default 30) until that fetch
 * resolves. VITE_SESSION_WARNING_SECONDS configures the warning dialog duration.
 *
 * Shows a warning modal before auto-logout, giving the user
 * a chance to extend their session.
 *
 * This component should wrap the main app content inside the AuthProvider.
 */
const InactivityLogoutWrapper = ({ children }) => {
  const { user, logout } = useAuth()

  // Recomputed on every render (cheap) so it reflects the latest fetched
  // platform setting — sessionActivity's internal value updates outside React,
  // and this component re-renders whenever AuthContext's value changes (e.g.
  // once `loading` flips after the boot-time fetch resolves).
  const sessionTotalTimeoutMs = getSessionTimeoutMs()
  const inactivityTimeoutMs = Math.max(MIN_INACTIVITY_TIMEOUT_MS, sessionTotalTimeoutMs - warningDurationMs)

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log(`[Session] Timeout configured: ${Math.round(sessionTotalTimeoutMs / 60000)} minutes total (${Math.round(inactivityTimeoutMs / 1000)}s inactivity + ${SESSION_WARNING_SECONDS}s warning)`)
    }
  }, [sessionTotalTimeoutMs, inactivityTimeoutMs])

  const { showWarning, secondsRemaining, extendSession } = useInactivityLogout({
    isAuthenticated: !!user,
    onLogout: logout,
    inactivityTimeout: inactivityTimeoutMs,
    warningDuration: warningDurationMs,
  })

  return (
    <>
      {children}
      <InactivityWarningModal
        isOpen={showWarning}
        secondsRemaining={secondsRemaining}
        onExtendSession={extendSession}
      />
    </>
  )
}

export default InactivityLogoutWrapper
